/**
 * @file useInfiniteScroll
 * @description 通用「触底加载」Hook。
 *
 * - 监听指定滚动容器 containerRef 的 scroll 事件
 * - 距离底部 `threshold` 像素内、仍有下一页时调用 `onLoadMore`
 * - 依赖变化时主动检查一次：首屏内容不足以撑起滚动条也能连锁触发下一页
 * - 通过 ref 保存最新回调，避免因回调引用变化频繁重绑监听器
 *
 * @example
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null);
 *
 * useInfiniteScroll({
 *     containerRef: scrollRef,
 *     hasMore,
 *     isFetchingNextPage,
 *     onLoadMore: fetchNextPage,
 * });
 *
 * return <div ref={scrollRef}>...</div>;
 * ```
 */

import {useEffect, useRef} from 'react';
import type {RefObject} from 'react';

export interface UseInfiniteScrollOptions {
    /** 滚动容器 ref。不传则监听 window 滚动 */
    containerRef?: RefObject<HTMLElement | null>;
    /** 是否还有下一页；false 时不触发 */
    hasMore: boolean;
    /** 是否正在加载下一页；避免重复触发 */
    isFetchingNextPage: boolean;
    /** 首页刷新态（可选）；为 true 时不触发下一页，避免与首页 refetch 竞态 */
    isRefreshing?: boolean;
    /** 触底加载回调 */
    onLoadMore: () => void;
    /** 距离底部多少像素触发，默认 80 */
    threshold?: number;
    /** 是否启用监听，默认 true */
    enabled?: boolean;
}

export function useInfiniteScroll({
    containerRef,
    hasMore,
    isFetchingNextPage,
    isRefreshing = false,
    onLoadMore,
    threshold = 80,
    enabled = true,
}: UseInfiniteScrollOptions): void {
    // 保存最新回调，避免因回调引用变化频繁重绑监听器
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        if (!enabled) {
            return () => {};
        }
        const el = containerRef?.current;
        // 显式传了 ref 但节点尚未挂载：等下次依赖变化再重试
        if (containerRef && !el) {
            return () => {};
        }
        const target: HTMLElement | Window = el ?? window;

        const isNearBottom = (): boolean => {
            if (target === window) {
                const scrollTop = window.scrollY;
                const clientHeight = window.innerHeight;
                const scrollHeight = document.documentElement.scrollHeight;
                return scrollHeight - scrollTop - clientHeight < threshold;
            }
            const node = target as HTMLElement;
            return node.scrollHeight - node.scrollTop - node.clientHeight < threshold;
        };

        const maybeLoadMore = () => {
            if (!hasMore || isFetchingNextPage || isRefreshing) {
                return;
            }
            if (isNearBottom()) {
                onLoadMoreRef.current();
            }
        };

        // 依赖变化时先主动检查一次（首屏未撑满时的连锁补拉）
        maybeLoadMore();

        target.addEventListener('scroll', maybeLoadMore, {passive: true});
        return () => {
            target.removeEventListener('scroll', maybeLoadMore);
        };
    }, [containerRef, hasMore, isFetchingNextPage, isRefreshing, threshold, enabled]);
}
